import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

const BASE_URL = (
  typeof import.meta !== "undefined"
    ? (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL
    : undefined
) ?? "";

export function createApiClient(config?: AxiosRequestConfig): AxiosInstance {
  const client = axios.create({
    baseURL: BASE_URL,
    timeout: 90_000,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    ...config,
  });

  client.interceptors.request.use((req) => {
    const token = localStorage.getItem("yrbp-access-token");
    if (token && req.headers) {
      req.headers["Authorization"] = `Bearer ${token}`;
    }
    // Let browser set Content-Type with boundary for FormData
    if (req.data instanceof FormData) {
      delete req.headers["Content-Type"];
    }
    return req;
  });

  // Single in-flight refresh promise — prevents race condition on simultaneous 401s
  let _refreshing: Promise<string> | null = null;

  const refresh = (): Promise<string> => {
    if (_refreshing) return _refreshing;
    _refreshing = (async () => {
      const refreshToken = localStorage.getItem("yrbp-refresh-token");
      if (!refreshToken) throw new Error("no refresh token");
      const res = await axios.post(`${BASE_URL}/api/v1/auth/refresh/`, {
        refresh: refreshToken,
      });
      const { access, refresh: newRefresh } = res.data;
      localStorage.setItem("yrbp-access-token", access);
      if (newRefresh) localStorage.setItem("yrbp-refresh-token", newRefresh);
      // Notify auth store so isAuthenticated() picks up the new token
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("yrbp:token-refreshed", { detail: { access } }));
      }
      return access as string;
    })().finally(() => { _refreshing = null; });
    return _refreshing;
  };

  client.interceptors.response.use(
    (res) => res,
    async (error: unknown) => {
      const status = (error as any)?.response?.status;
      const originalReq = (error as any)?.config;

      if (status === 429) {
        const retryAfter = (error as any)?.response?.headers?.["retry-after"];
        const wait = retryAfter ? parseInt(retryAfter) * 1000 : 60_000;
        return Promise.reject(
          Object.assign(error as object, {
            _rateLimited: true,
            _retryAfterMs: wait,
            message: `Juda ko'p so'rov. ${Math.ceil(wait / 1000)}s kutib qayta urinib ko'ring.`,
          })
        );
      }

      if (status === 401 && originalReq && !originalReq._retried) {
        originalReq._retried = true;
        try {
          const newToken = await refresh();
          originalReq.headers["Authorization"] = `Bearer ${newToken}`;
          return client(originalReq);
        } catch {
          localStorage.removeItem("yrbp-access-token");
          localStorage.removeItem("yrbp-refresh-token");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}

export const apiClient = createApiClient();
